import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import {
  setAltchaWidgetRef,
  captchaSuccess,
  captchaError,
  captchaChallengeFetch,
  getVerifySource,
  setVerifySource,
  setVerifyInFlight,
  CaptchaVerifySource,
} from '../../reducers/CaptchaReducer/CaptchaActions';
import { setCaptchaExpired } from '../../reducers/CaptchaReducer/CaptchaReducer';
import { getCaptchaExpiredSelector } from '../../reducers/CaptchaReducer/CaptchaSelector';
import { StateType } from '../../reducers';
import { API_URL } from '../../config';
import './Altcha.scss';
import 'altcha';
import { useAppDispatch } from '../../shared/services/store/useAppDispatch';

const ALTCHA_INITIAL_VERIFY_DELAY_MS = 1000;

const CHALLENGE_URL = `${API_URL}/captcha`;

// Well-known DOM id for the portal target where the widget renders when expired
export const ALTCHA_PORTAL_TARGET_ID = 'altcha-portal-target';

export const AltchaWidget: React.FC = () => {
  const widgetRef = useRef<HTMLElement>(null);
  const dispatch = useAppDispatch();
  const isExpired = useSelector((state: StateType) => getCaptchaExpiredSelector(state));

  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) {
      return;
    }

    setAltchaWidgetRef(widget);

    // Property (not JSX attribute) since it's a function; set before the pre-compute timer.
    (widget as any).customfetch = captchaChallengeFetch;

    const mountedAt = performance.now();

    // Track when the current verify() began so we can report PoW duration
    // alongside any error. Reset on each 'verifying' transition.
    let verifyStartedAt: number | null = null;
    // True while a verify is in progress (set on 'verifying', cleared on
    // terminal states). Used to detect overlap when a new verify() starts
    // while one is still running.
    let inFlight = false;
    let inFlightSource: CaptchaVerifySource | null = null;
    // Captured on the next 'verifying' transition: was a verify already
    // in flight when this one started? Read on 'error' to flag the overlap.
    let overlapAtStart = false;
    let previousInFlightSource: CaptchaVerifySource | null = null;
    let preComputeTimer: ReturnType<typeof setTimeout> | null = null;

    const cancelPreComputeTimer = () => {
      if (preComputeTimer !== null) {
        clearTimeout(preComputeTimer);
        preComputeTimer = null;
      }
    };

    const handleStateChange = (ev: Event) => {
      const detail = (ev as CustomEvent).detail;
      const state = detail?.state;

      if (state === 'verified' && detail?.payload) {
        dispatch(setCaptchaExpired(false));
        verifyStartedAt = null;
        inFlight = false;
        inFlightSource = null;
        setVerifyInFlight(false);
        // Dispatch success last so any foreground call attached to the
        // in-flight verify sees verifyInFlight=false in subsequent reads.
        dispatch(captchaSuccess(detail.payload));
      } else if (state === 'error') {
        const elapsedMs = verifyStartedAt !== null ? performance.now() - verifyStartedAt : null;
        const msSinceMount = performance.now() - mountedAt;
        const verifySource = getVerifySource();
        verifyStartedAt = null;
        inFlight = false;
        inFlightSource = null;
        setVerifyInFlight(false);
        // Forward the full statechange detail so Sentry gets the challenge
        // shape (challenge, salt, maxnumber, signature) and any worker
        // diagnostics. This is the data we need to distinguish the
        // boundary case from a malformed challenge or a worker bail-out.
        dispatch(
          captchaError(detail?.message || 'Captcha verification failed', {
            detail,
            elapsedMs,
            verifySource,
            msSinceMount,
            verifyOverlap: overlapAtStart,
            previousInFlightSource,
          })
        );
        overlapAtStart = false;
        previousInFlightSource = null;
        setVerifySource(null);
      } else if (state === 'expired') {
        dispatch(setCaptchaExpired(true));
      } else if (state === 'verifying') {
        // Any verify in progress means we no longer need the on-mount
        // pre-compute. Cancel it to avoid the precompute-vs-foreground
        // race that surfaces as "Unexpected result returned." when the
        // 3s timer fires into a foreground verify already running.
        cancelPreComputeTimer();
        dispatch(setCaptchaExpired(false));
        verifyStartedAt = performance.now();
        // Detect overlap: a verify() was already running when this one began.
        // Both races we care about (precompute vs foreground, foreground vs
        // background-refresh) surface here.
        overlapAtStart = inFlight;
        previousInFlightSource = inFlightSource;
        inFlight = true;
        inFlightSource = getVerifySource();
        setVerifyInFlight(true);
      }
    };

    widget.addEventListener('statechange', handleStateChange);

    // Pre-compute PoW immediately after mount so the solution is ready
    // when the user clicks sign-in. Short delay ensures the web component
    // is fully initialised before we call its API. Skip if a verify is
    // already in flight (e.g., user clicked sign-in within the delay
    // window) — the statechange handler will also cancel this timer the
    // moment a verify starts.
    preComputeTimer = setTimeout(() => {
      preComputeTimer = null;
      if (inFlight) {
        return;
      }
      setVerifySource('precompute');
      (widget as any).verify?.();
    }, ALTCHA_INITIAL_VERIFY_DELAY_MS);

    return () => {
      cancelPreComputeTimer();
      widget.removeEventListener('statechange', handleStateChange);
      setAltchaWidgetRef(null);
    };
  }, [dispatch]);

  const portalTarget = isExpired ? document.getElementById(ALTCHA_PORTAL_TARGET_ID) : null;

  const widgetElement = (
    <div className={`altcha-container${isExpired ? ' altcha-container--visible' : ''}`}>
      {/* @ts-expect-error custom element altcha-widget not in JSX intrinsic elements */}
      <altcha-widget ref={widgetRef} challengeurl={CHALLENGE_URL} hidefooter hidelogo />
    </div>
  );

  // When expired and a portal target exists in a form, render the widget there.
  // Otherwise render it in its default (hidden) location.
  if (portalTarget) {
    return ReactDOM.createPortal(widgetElement, portalTarget);
  }

  return widgetElement;
};

// Inline portal target — place this in forms that use captcha.
// When the captcha expires, the AltchaWidget will portal into this element.
export const AltchaCaptchaPortalTarget: React.FC = () => {
  const isExpired = useSelector((state: StateType) => getCaptchaExpiredSelector(state));

  return <div id={ALTCHA_PORTAL_TARGET_ID} className={`altcha-portal-target${isExpired ? ' altcha-portal-target--active' : ''}`} />;
};

// App-level initializer — renders the Altcha widget globally (hidden by default)
export const AppInitAltcha: React.FC = () => {
  return <AltchaWidget />;
};
